import { useRef, useState, useEffect, forwardRef } from "react";
import { toPng } from "html-to-image";
import QRCode from "qrcode";
import { ResponsiveContainer, LineChart, Line } from "recharts";
import { Share2, Download, Loader2, Check, Building2, TrendingUp, Sparkles, Link as LinkIcon, Copy, Square, RectangleVertical } from "lucide-react";
import { fmtMoney } from "@/lib/api";
import { Img } from "@/components/Img";
import { WatermarkPill } from "@/components/Watermark";

const V_COLOR = { GO: "#10B981", MAYBE: "#F59E0B", PASS: "#E76F51" };
const MODES = {
  arc: { w: 1080, h: 1080, scale: 0.45, label: "Story Arc", icon: Square, fileTag: "arc" },
  yard: { w: 1080, h: 1920, scale: 0.27, label: "Yard Sign", icon: RectangleVertical, fileTag: "yardsign" },
};

export default function ShareArc({ leadAddress, leadCityState, points, headline, agentName, brandLogo, brandAccent, brandCompany, leadId, reportToken, showWatermark = false, onClose }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("idle");
  const [shortlink, setShortlink] = useState(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [yardQrDataUrl, setYardQrDataUrl] = useState(null);
  const [mode, setMode] = useState("arc");

  const accent = brandAccent || "#10B981";
  const company = brandCompany || agentName || "iSpeedToClose";
  const M = MODES[mode];

  // Lazily create a tracked shortlink for this report when the modal opens
  useEffect(() => {
    if (!leadId || !reportToken) return;
    let alive = true;
    import("@/lib/api").then(({ api }) =>
      api.post("/shortlinks", { lead_id: leadId, kind: "report", target_token: reportToken })
        .then((r) => { if (alive) setShortlink(r.data); })
        .catch(() => {})
    );
    return () => { alive = false; };
  }, [leadId, reportToken]);

  // Two QR sizes: small (132px on the arc card) and giant (760px on yard sign)
  useEffect(() => {
    if (!shortlink?.redirect_url) { setQrDataUrl(null); setYardQrDataUrl(null); return; }
    const url = `${shortlink.redirect_url}?utm_source=qr`;
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "H", margin: 1, width: 360,
      color: { dark: accent, light: "#ffffff" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "H", margin: 2, width: 760,
      color: { dark: "#0B132B", light: "#ffffff" },
    }).then(setYardQrDataUrl).catch(() => setYardQrDataUrl(null));
  }, [shortlink, accent]);

  const trackedUrl = shortlink?.redirect_url
    ? `${shortlink.redirect_url}?utm_source=share_arc`
    : null;
  // Short label shown ON the image (no protocol — cleaner)
  const displayUrl = shortlink?.redirect_url
    ? shortlink.redirect_url.replace(/^https?:\/\//, "").replace(/\/api\/d\//, "/")
    : null;

  const copyUrl = async () => {
    if (!trackedUrl) return;
    try {
      await navigator.clipboard.writeText(trackedUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const first = points[0];
  const last = points[points.length - 1];
  const delta = (last.projected_profit ?? 0) - (first.projected_profit ?? 0);
  const chartData = points.map((p, i) => ({ i: i + 1, profit: p.projected_profit ?? 0 }));

  const capture = async () => {
    if (!ref.current) return null;
    return await toPng(ref.current, {
      pixelRatio: 2,
      backgroundColor: "#0B132B",
      cacheBust: true,
      width: M.w,
      height: M.h,
    });
  };

  const filename = () =>
    `ispeedtoclose-${M.fileTag}-${(leadAddress || "deal").replace(/\W+/g, "-").toLowerCase()}.png`;

  const onShare = async () => {
    setBusy(true);
    try {
      const dataUrl = await capture();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename(), { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: mode === "yard" ? "Yard sign" : "Deal arc",
          text: `${leadAddress} — ${headline}`,
        });
        setStatus("shared");
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename();
        a.click();
        setStatus("downloaded");
      }
    } catch {
      // share/download silently fails — finally-block still cleans state
    } finally {
      setBusy(false);
      setTimeout(() => setStatus("idle"), 2500);
    }
  };

  const onDownload = async () => {
    setBusy(true);
    try {
      const dataUrl = await capture();
      if (!dataUrl) return;
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename();
      a.click();
      setStatus("downloaded");
    } finally {
      setBusy(false);
      setTimeout(() => setStatus("idle"), 2500);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="share-arc-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
          <div>
            <div className="label-eyebrow flex items-center gap-1.5"><Share2 size={11} /> Share this arc</div>
            <h2 className="font-display font-bold text-xl mt-1">
              {mode === "yard" ? "Print a yard sign" : "Drop into SMS, WhatsApp, or DM"}
            </h2>
          </div>
          <div
            role="tablist"
            aria-label="Share format"
            className="inline-flex p-1 rounded-lg"
            style={{ background: "rgba(15,23,42,0.06)" }}
            data-testid="share-arc-mode-tabs"
          >
            {Object.entries(MODES).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const active = mode === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMode(key)}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold transition flex items-center gap-1.5"
                  style={{
                    background: active ? "var(--ink)" : "transparent",
                    color: active ? "#fff" : "var(--ink-2)",
                  }}
                  data-testid={`share-arc-mode-${key}`}
                >
                  <Icon size={13} /> {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Preview — clipped wrapper scales the full-size card down */}
          <div
            className="rounded-xl shadow-lg mb-5 mx-auto"
            style={{
              width: M.w * M.scale,
              height: M.h * M.scale,
              overflow: "hidden",
              background: "#0B132B",
            }}
          >
            <div style={{ transform: `scale(${M.scale})`, transformOrigin: "top left", width: M.w, height: M.h }}>
              {mode === "yard" ? (
                <YardSignCard
                  ref={ref}
                  leadAddress={leadAddress}
                  leadCityState={leadCityState}
                  last={last}
                  delta={delta}
                  brandLogo={brandLogo}
                  brandAccent={accent}
                  brandCompany={company}
                  displayUrl={displayUrl}
                  qrDataUrl={yardQrDataUrl}
                  showWatermark={showWatermark}
                />
              ) : (
                <ShareCard
                  ref={ref}
                  leadAddress={leadAddress}
                  leadCityState={leadCityState}
                  points={points}
                  first={first}
                  last={last}
                  delta={delta}
                  chartData={chartData}
                  headline={headline}
                  agentName={agentName}
                  brandLogo={brandLogo}
                  brandAccent={accent}
                  brandCompany={company}
                  displayUrl={displayUrl}
                  qrDataUrl={qrDataUrl}
                  showWatermark={showWatermark}
                />
              )}
            </div>
          </div>

          <div className="flex gap-3 flex-wrap">
            <button type="button" className="btn-money flex-1 justify-center" onClick={onShare} disabled={busy} data-testid="share-arc-share-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> :
                status === "shared" ? <Check size={14} /> :
                <Share2 size={14} />}
              {busy ? "Generating…" : status === "shared" ? "Shared!" : "Share"}
            </button>
            <button type="button" className="btn-outline flex-1 justify-center" onClick={onDownload} disabled={busy} data-testid="share-arc-download-button">
              {status === "downloaded" ? <Check size={14} /> : <Download size={14} />}
              {status === "downloaded" ? "Downloaded" : "Download PNG"}
            </button>
            <button type="button" className="btn-outline" onClick={onClose}>Close</button>
          </div>

          {/* Tracked short URL row */}
          {trackedUrl && (
            <div
              className="mt-4 rounded-xl border p-3 flex items-center gap-3"
              style={{ borderColor: "var(--line)", background: "rgba(15,23,42,0.03)" }}
              data-testid="share-arc-tracked-url"
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: accent + "1a", color: accent }}
              >
                <LinkIcon size={15} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="label-eyebrow">Tracked share link</div>
                <div className="font-mono text-sm truncate mt-0.5" data-testid="share-arc-tracked-url-text">
                  {trackedUrl}
                </div>
                <div className="text-[11px] text-[var(--ink-3)] mt-1">
                  Tracked clicks/scans in <strong>Analytics</strong> · click = <code>share_arc</code>, scan = <code>qr</code>.
                </div>
              </div>
              <button
                type="button"
                onClick={copyUrl}
                className="btn-outline !py-2 !px-3 shrink-0"
                data-testid="share-arc-copy-url-button"
              >
                {urlCopied ? <Check size={13} /> : <Copy size={13} />}
                {urlCopied ? "Copied" : "Copy"}
              </button>
            </div>
          )}

          <p className="text-xs text-[var(--ink-3)] mt-3 italic">
            {mode === "yard"
              ? "Print at 18\"×32\" on corrugated plastic or send to your sign vendor. The QR survives weather, sun, and 8' viewing distance."
              : "On mobile, \"Share\" opens your system share sheet (iMessage, WhatsApp, etc). On desktop, it downloads the PNG."}
          </p>
        </div>
      </div>
    </div>
  );
}

const ShareCard = forwardRef(function ShareCard({ leadAddress, leadCityState, points, first, last, delta, chartData, headline, agentName, brandLogo, brandAccent, brandCompany, displayUrl, qrDataUrl, showWatermark }, ref) {
  const accent = brandAccent || "#10B981";
  const accentRgba = (alpha) => hexToRgba(accent, alpha);
  return (
    <div
      ref={ref}
      style={{
        width: 1080, height: 1080,
        background: `linear-gradient(135deg, #0B132B 0%, #1B2440 60%, ${darken(accent, 0.6)} 100%)`,
        color: "#fff",
        position: "relative",
        fontFamily: '"Cabinet Grotesk", "Manrope", system-ui, sans-serif',
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: -200, right: -200, width: 600, height: 600, borderRadius: "50%",
                    background: `radial-gradient(circle, ${accentRgba(0.32)} 0%, transparent 70%)` }} />

      {showWatermark && (
        <div style={{ position: "absolute", bottom: 24, right: 24, zIndex: 2 }}>
          <WatermarkPill inverted />
        </div>
      )}

      <div style={{ position: "absolute", inset: 0, padding: 72, display: "flex", flexDirection: "column" }}>
        {/* Brand row */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
          <div style={{ width: 72, height: 72, borderRadius: 16,
                        background: brandLogo ? "#fff" : accent,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        overflow: "hidden", padding: brandLogo ? 8 : 0 }}>
            {brandLogo ? (
              <Img src={brandLogo} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} crossOrigin="anonymous" />
            ) : (
              <Building2 size={32} color="#fff" />
            )}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 32, letterSpacing: "-0.02em", lineHeight: 1 }}>
              {brandCompany || "iSpeedToClose"}
            </div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.18em", marginTop: 6 }}>
              Deal Story Arc
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8,
                        padding: "10px 18px", borderRadius: 999,
                        background: accentRgba(0.2), border: `1px solid ${accentRgba(0.45)}`,
                        color: lighten(accent, 0.5), fontSize: 18, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.12em" }}>
            <Sparkles size={16} /> {last.verdict || "—"}
          </div>
        </div>

        {/* Address */}
        <div style={{ marginTop: 56, position: "relative", zIndex: 1 }}>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 18, letterSpacing: "0.04em" }}>
            {leadCityState || "—"}
          </div>
          <div style={{ fontSize: 64, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.02, marginTop: 8 }}>
            {leadAddress}
          </div>
        </div>

        {/* Headline */}
        <div style={{ marginTop: 36, fontSize: 28, lineHeight: 1.32, color: "rgba(255,255,255,0.92)",
                      maxWidth: 880, position: "relative", zIndex: 1 }}>
          {headline}
        </div>

        {/* Profit delta + sparkline */}
        <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "auto 1fr", gap: 40, alignItems: "center",
                      position: "relative", zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.2em", marginBottom: 10 }}>
              Profit delta
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
              <span style={{ fontSize: 38, color: "rgba(255,255,255,0.7)", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {fmtMoney(first.projected_profit)}
              </span>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 28 }}>→</span>
              <span style={{ fontSize: 76, color: accent, fontWeight: 900, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                {fmtMoney(last.projected_profit)}
              </span>
            </div>
            <div style={{ marginTop: 8, fontSize: 22, fontWeight: 700, color: delta >= 0 ? lighten(accent, 0.5) : "#FCA5A5",
                          display: "inline-flex", alignItems: "center", gap: 6 }}>
              <TrendingUp size={20} /> {delta >= 0 ? "+" : "−"}{fmtMoney(Math.abs(delta))}
            </div>
          </div>

          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 12, right: 12, left: 12, bottom: 12 }}>
                <Line type="monotone" dataKey="profit" stroke={accent} strokeWidth={5}
                      dot={{ r: 8, fill: accent, stroke: "#0B132B", strokeWidth: 3 }}
                      isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Verdict chain */}
        <div style={{ marginTop: 36, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                      position: "relative", zIndex: 1 }}>
          {points.map((p, i) => {
            const c = p.verdict === last.verdict ? accent : (V_COLOR[p.verdict] || "#94A3B8");
            return (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  padding: "8px 16px", borderRadius: 999, fontSize: 18, fontWeight: 800,
                  background: hexToRgba(c, 0.13),
                  color: c,
                  border: `1px solid ${hexToRgba(c, 0.47)}`,
                }}>
                  {p.verdict || "—"}
                </span>
                {i < points.length - 1 && <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 24 }}>→</span>}
              </span>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ marginTop: "auto", paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.12)",
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24,
                      color: "rgba(255,255,255,0.55)", fontSize: 16, position: "relative", zIndex: 1 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ marginBottom: 8 }}>Prepared by <strong style={{ color: "#fff", fontWeight: 700 }}>{agentName || brandCompany || "iSpeedToClose"}</strong></div>
            {displayUrl ? (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10,
                            padding: "10px 18px", borderRadius: 999,
                            background: hexToRgba(brandAccent || "#10B981", 0.18),
                            border: `1px solid ${hexToRgba(brandAccent || "#10B981", 0.45)}`,
                            color: "#fff", fontWeight: 700, fontSize: 20,
                            fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" }}>
                <span style={{ width: 9, height: 9, borderRadius: 5, background: brandAccent || "#10B981" }} />
                {displayUrl}
              </div>
            ) : (
              <div>{brandCompany ? "Powered by iSpeedToClose" : "ispeedtoclose · speed is the deal"}</div>
            )}
          </div>
          {qrDataUrl && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <div style={{ background: "#fff", borderRadius: 16, padding: 14,
                            boxShadow: `0 0 0 3px ${hexToRgba(brandAccent || "#10B981", 0.45)}` }}>
                <Img src={qrDataUrl} alt="" style={{ width: 132, height: 132, display: "block" }} crossOrigin="anonymous" />
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.2em" }}>
                Scan to view
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// ───────── color utilities (inline, no deps)
function hexToRgba(hex, alpha) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return `rgba(16,185,129,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
function lighten(hex, amt) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const r = Math.min(255, Math.round(parseInt(h.slice(0, 2), 16) + (255 - parseInt(h.slice(0, 2), 16)) * amt));
  const g = Math.min(255, Math.round(parseInt(h.slice(2, 4), 16) + (255 - parseInt(h.slice(2, 4), 16)) * amt));
  const b = Math.min(255, Math.round(parseInt(h.slice(4, 6), 16) + (255 - parseInt(h.slice(4, 6), 16)) * amt));
  return `rgb(${r},${g},${b})`;
}
function darken(hex, amt) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const r = Math.max(0, Math.round(parseInt(h.slice(0, 2), 16) * (1 - amt)));
  const g = Math.max(0, Math.round(parseInt(h.slice(2, 4), 16) * (1 - amt)));
  const b = Math.max(0, Math.round(parseInt(h.slice(4, 6), 16) * (1 - amt)));
  return `rgb(${r},${g},${b})`;
}

// ═══════════════════════ Yard-sign mode — 1080×1920 portrait ═══════════════════════
//
// Designed to be readable from 8–10 feet away. Massive QR, huge headline, address
// + accent-tinted profit chip, white plastic-friendly background, brand strip on top.
const YardSignCard = forwardRef(function YardSignCard({
  leadAddress, leadCityState, last, delta, brandLogo, brandAccent, brandCompany, displayUrl, qrDataUrl, showWatermark,
}, ref) {
  const accent = brandAccent || "#10B981";
  const isGo = (last?.verdict || "").toUpperCase() === "GO";
  return (
    <div
      ref={ref}
      style={{
        width: 1080, height: 1920,
        background: "#ffffff",
        color: "#0B132B",
        position: "relative",
        fontFamily: '"Cabinet Grotesk", "Manrope", system-ui, sans-serif',
        overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}
    >
      {/* Top brand bar — accent block for instant brand recognition */}
      <div style={{
        background: accent,
        padding: "44px 64px",
        display: "flex", alignItems: "center", gap: 24,
        color: "#fff",
        position: "relative",
      }}>
        {showWatermark && (
          <div style={{ position: "absolute", top: 12, right: 16 }}>
            <WatermarkPill inverted />
          </div>
        )}
        <div style={{
          width: 96, height: 96, borderRadius: 20,
          background: brandLogo ? "#fff" : "rgba(255,255,255,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: brandLogo ? 10 : 0, overflow: "hidden",
        }}>
          {brandLogo ? (
            <Img src={brandLogo} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} crossOrigin="anonymous" />
          ) : (
            <Building2 size={48} color="#fff" />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 44, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1 }}>
            {brandCompany || "iSpeedToClose"}
          </div>
          <div style={{ fontSize: 18, opacity: 0.85, textTransform: "uppercase",
                        letterSpacing: "0.22em", marginTop: 10, fontWeight: 700 }}>
            We pay cash for houses
          </div>
        </div>
      </div>

      {/* Headline */}
      <div style={{ padding: "72px 64px 0", position: "relative" }}>
        <div style={{ fontSize: 32, color: accent, fontWeight: 800,
                      textTransform: "uppercase", letterSpacing: "0.3em", marginBottom: 20 }}>
          {isGo ? "Investor Deal" : "Cash Offer"}
        </div>
        <div style={{ fontSize: 132, fontWeight: 900, letterSpacing: "-0.04em",
                      lineHeight: 0.92, color: "#0B132B" }}>
          {leadAddress}
        </div>
        {leadCityState && (
          <div style={{ fontSize: 36, color: "#5B6478", marginTop: 24, fontWeight: 600 }}>
            {leadCityState}
          </div>
        )}
        {Number.isFinite(last?.projected_profit) && last.projected_profit > 0 && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 14,
                        marginTop: 36, padding: "16px 28px", borderRadius: 16,
                        background: hexToRgba(accent, 0.12),
                        border: `2px solid ${hexToRgba(accent, 0.35)}`,
                        color: darken(accent, 0.15), fontWeight: 800, fontSize: 30 }}>
            <TrendingUp size={28} /> Projected: {fmtMoney(last.projected_profit)}
          </div>
        )}
      </div>

      {/* QR — the star of the show */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", padding: "0 64px" }}>
        {qrDataUrl ? (
          <>
            <div style={{
              background: "#fff", padding: 28, borderRadius: 32,
              boxShadow: `0 0 0 8px ${accent}, 0 24px 60px rgba(11,19,43,0.25)`,
            }}>
              <Img src={qrDataUrl} alt="" style={{ width: 580, height: 580, display: "block" }} crossOrigin="anonymous" />
            </div>
            <div style={{ marginTop: 36, fontSize: 56, fontWeight: 900,
                          textAlign: "center", color: "#0B132B", letterSpacing: "-0.02em" }}>
              Scan to see the deal
            </div>
            <div style={{ marginTop: 12, fontSize: 24, color: "#5B6478", textAlign: "center", fontWeight: 600 }}>
              Photos · price · payout terms — no calls, no commitment.
            </div>
          </>
        ) : (
          <div style={{ width: 580, height: 580, background: "#F1F2F6", borderRadius: 32 }} />
        )}
      </div>

      {/* Footer strip — URL backup for non-scanners + accent block */}
      <div style={{ background: "#0B132B", color: "#fff",
                    padding: "32px 64px",
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
        <div style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
                      fontSize: 28, fontWeight: 700, color: "#fff",
                      minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayUrl || "ispeedtoclose.com"}
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
                      color: accent, fontSize: 20, fontWeight: 800,
                      textTransform: "uppercase", letterSpacing: "0.2em" }}>
          <Sparkles size={18} /> Powered by iSpeedToClose
        </div>
      </div>
    </div>
  );
});
